import React from 'react';

/**
 * Inline SVG icons. No icon library is installed and none should be added —
 * every icon in this codebase is an inline stroke SVG and these match.
 *
 * All icons are 24x24 viewBox, `stroke="currentColor"`, sized by className so a
 * call site can render them at 12, 16, 18 or 20px without a prop.
 */

export interface IconProps {
  className?: string;
}

const base = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: 'false' as const,
};

const svg = (d: string | string[]) => {
  const paths = Array.isArray(d) ? d : [d];
  const Component: React.FC<IconProps> = ({ className = 'w-[18px] h-[18px]' }) => (
    <svg {...base} className={className}>
      {paths.map((p, i) => (
        <path key={i} d={p} />
      ))}
    </svg>
  );
  return Component;
};

export const BellIcon = svg([
  'M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-4-5.7V5a2 2 0 1 0-4 0v.3A6 6 0 0 0 6 11v3.2a2 2 0 0 1-.6 1.4L4 17h11Z',
  'M9.5 20a2.5 2.5 0 0 0 5 0',
]);

export const ListIcon = svg(['M8 6h13M8 12h13M8 18h13', 'M3.5 6h.01M3.5 12h.01M3.5 18h.01']);

export const BoltIcon = svg('M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z');

export const TicketIcon = svg([
  'M3 9.5V7a1.5 1.5 0 0 1 1.5-1.5h15A1.5 1.5 0 0 1 21 7v2.5a2.5 2.5 0 0 0 0 5V17a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17v-2.5a2.5 2.5 0 0 0 0-5Z',
  'M12 8.5v7',
]);

export const TagIcon = svg([
  'M20.5 13.3 13.3 20.5a1.5 1.5 0 0 1-2.1 0l-7.7-7.7V4a.5.5 0 0 1 .5-.5h8.8l7.7 7.7a1.5 1.5 0 0 1 0 2.1Z',
  'M7.5 7.5h.01',
]);

export const TransferIcon = svg(['M4 8h13l-3-3', 'M20 16H7l3 3']);

export const LinkIcon = svg([
  'M10 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1 1',
  'M14 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1-1',
]);

export const PhoneIcon = svg([
  'M7 2.5h10a1.5 1.5 0 0 1 1.5 1.5v16a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 20V4A1.5 1.5 0 0 1 7 2.5Z',
  'M11 18.5h2',
]);

export const UserIcon = svg([
  'M12 11.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  'M4.5 20.5a7.5 7.5 0 0 1 15 0',
]);

export const TrashIcon = svg([
  'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
]);

export const CardIcon = svg([
  'M3.5 6.5h17a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z',
  'M2.5 10.5h19',
  'M6 14.5h3',
]);

export const BookIcon = svg([
  'M4 4.5h6a2 2 0 0 1 2 2v13a2 2 0 0 0-2-2H4v-13Z',
  'M20 4.5h-6a2 2 0 0 0-2 2v13a2 2 0 0 1 2-2h6v-13Z',
]);

export const HelpIcon = svg([
  'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  'M9.8 9.5a2.2 2.2 0 1 1 3.2 2 1.8 1.8 0 0 0-1 1.6v.4',
  'M12 17h.01',
]);

export const SearchIcon = svg(['M20 20l-4.5-4.5', 'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13Z']);

export const CalendarIcon = svg([
  'M4.5 8.5h15M7.5 4.5v3M16.5 4.5v3',
  'M5.5 6.5h13a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1Z',
]);

export const LockIcon = svg([
  'M6.5 10.5h11a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
  'M8.5 10.5V7a3.5 3.5 0 0 1 7 0v3.5',
]);

export const ChevronDownIcon = svg('M6 9.5l6 6 6-6');
export const ChevronRightIcon = svg('M9.5 6l6 6-6 6');
export const CloseIcon = svg('M6 6l12 12M18 6L6 18');
export const MenuIcon = svg('M4 7h16M4 12h16M4 17h16');
export const CheckIcon = svg('M5 12.5l4.5 4.5L19 7.5');
export const ArrowRightIcon = svg(['M4 12h15', 'M14 7l5 5-5 5']);
export const ExternalIcon = svg(['M14 4.5h5.5V10', 'M19.5 4.5 11 13', 'M18 14v5.5H4.5V6H10']);
export const PlusIcon = svg('M12 5v14M5 12h14');
/** Two people: the Seats nav item. UserIcon, plus a smaller one behind it. */
export const UsersIcon = svg([
  'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
  'M3 20a7 7 0 0 1 14 0',
  'M16.5 11.2a3 3 0 0 0 0-5.9',
  'M18 20a5.6 5.6 0 0 0-1.4-3.7',
]);
export const SlidersIcon = svg([
  'M4 7h10M18 7h2M4 17h2M10 17h10',
  'M16 7a2 2 0 1 0 0-.01M8 17a2 2 0 1 0 0-.01',
]);

/** Lifebuoy: the Support nav item. A ring with four spokes. */
export const LifebuoyIcon = svg([
  'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
  'M5.6 5.6l3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9',
]);

/** Nav-item icon registry. `routes.ts` stores the name; the sidebar renders it. */
export const NAV_ICONS = {
  bell: BellIcon,
  list: ListIcon,
  bolt: BoltIcon,
  ticket: TicketIcon,
  tag: TagIcon,
  transfer: TransferIcon,
  link: LinkIcon,
  phone: PhoneIcon,
  user: UserIcon,
  card: CardIcon,
  book: BookIcon,
  help: HelpIcon,
  plus: PlusIcon,
  users: UsersIcon,
  lifebuoy: LifebuoyIcon,
  calendar: CalendarIcon,
} as const;

export type NavIconName = keyof typeof NAV_ICONS;
